/**
 * FulfillmentContextDisplay Component
 * 
 * Displays fulfillment context_json with JSON/Structured toggle.
 * Generic display that adapts to any fulfillment type based on context keys.
 */

import React, { useState, useMemo } from 'react';
import { DataList } from '@/components/common/DataList';
import './FulfillmentContextDisplay.scss';

interface FulfillmentContextDisplayProps {
  context: Record<string, unknown> | null;
  rewardCategory?: string;
}

// Format value for display
function formatValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="null-value">—</span>;
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  if (typeof value === 'number') {
    // Check if it looks like money
    return value.toLocaleString();
  }
  
  if (typeof value === 'string') {
    // Check if it's a date string
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return new Date(value).toLocaleString();
    }
    return value;
  }
  
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  
  if (typeof value === 'object') {
    // For nested objects, show as formatted JSON
    return (
      <pre className="nested-object">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  
  return String(value);
}

// Convert snake_case to Title Case
function formatLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

// Keys that should be marked as copyable
const COPYABLE_KEYS = [
  'tracking_number',
  'confirmation_number',
  'external_tracking_id',
  'card_last_four',
  'error_code',
  'ticket_id',
];

// Keys that contain monetary values
const MONEY_KEYS = [
  'transfer_amount',
  'copay_amount',
  'covered_amount',
  'available_balance',
];

export const FulfillmentContextDisplay: React.FC<FulfillmentContextDisplayProps> = ({ 
  context,
  rewardCategory,
}) => {
  const [viewMode, setViewMode] = useState<'structured' | 'json'>('structured');

  // Build data list items from context
  const items = useMemo(() => {
    if (!context) return [];
    
    return Object.entries(context).map(([key, value]) => {
      let displayValue = formatValue(value);
      
      // Format money values
      if (MONEY_KEYS.includes(key) && typeof value === 'number') {
        displayValue = `$${value.toFixed(2)}`;
      }
      
      return {
        label: formatLabel(key),
        value: displayValue,
        copyable: COPYABLE_KEYS.includes(key),
      };
    });
  }, [context]);

  if (!context || Object.keys(context).length === 0) {
    return (
      <div className="fulfillment-context-display empty">
        <p className="no-context">No context data available</p>
      </div>
    );
  }

  return (
    <div className="fulfillment-context-display">
      <div className="context-header">
        <h4>
          Context Details
          {rewardCategory && (
            <span className="category-tag">{rewardCategory}</span>
          )}
        </h4>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'structured' ? 'active' : ''}`}
            onClick={() => setViewMode('structured')}
          >
            Structured
          </button>
          <button
            className={`toggle-btn ${viewMode === 'json' ? 'active' : ''}`}
            onClick={() => setViewMode('json')}
          >
            JSON
          </button>
        </div>
      </div>

      {viewMode === 'structured' ? (
        <DataList items={items} />
      ) : (
        <div className="json-view">
          <pre>{JSON.stringify(context, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default FulfillmentContextDisplay;

